import type { DevicePreferences, SharedDatabase } from './shared-db'

/**
 * Les réglages de l'appareil, **lus avec leurs défauts** — la seule porte vers la table
 * `preferences` (`offline/shared-db.ts`, la base que toutes les équipes partagent).
 *
 * ⚠️ Un module de `offline/`, et pas de `features/settings/` : ses lecteurs sont l'écran de
 * réglages **et** la couche de synchro (`offline/scheduler.ts`), et `offline/` ne peut pas
 * dépendre d'une feature — c'est la couche que tout le monde importe, jamais l'inverse.
 */

/**
 * Ce que fait une tablette qu'on n'a jamais réglée : **exactement ce qu'elle faisait avant que
 * l'écran n'existe**. Un défaut à `false` éteindrait le GPS et la synchro de fond de tout le
 * parc au déploiement suivant, sans que personne n'ait rien demandé.
 */
export const DEVICE_PREFERENCE_DEFAULTS: Required<Omit<DevicePreferences, 'id'>> = {
  autoGeolocation: true,
  autoSync: true,
}

export type ResolvedDevicePreferences = typeof DEVICE_PREFERENCE_DEFAULTS

/** Une tablette n'a qu'un jeu de réglages : la clé est fixe, et l'écriture écrase. */
const ROW_ID = 'current'

/**
 * ⚠️ **Ne rejette jamais.** Une base illisible — quota, mode privé, migration en cours — ne doit
 * ni empêcher une signature ni suspendre une passe de synchro : on retombe sur les défauts,
 * c'est-à-dire sur le comportement d'avant les réglages (M21).
 */
export async function readDevicePreferences(
  db: SharedDatabase,
): Promise<ResolvedDevicePreferences> {
  try {
    const stored = await db.preferences.get(ROW_ID)

    return {
      autoGeolocation: stored?.autoGeolocation ?? DEVICE_PREFERENCE_DEFAULTS.autoGeolocation,
      autoSync: stored?.autoSync ?? DEVICE_PREFERENCE_DEFAULTS.autoSync,
    }
  } catch (error) {
    console.error('[terrain] Réglages illisibles ; valeurs par défaut appliquées.', error)

    return { ...DEVICE_PREFERENCE_DEFAULTS }
  }
}

/**
 * Écrit **un** réglage sans toucher aux autres.
 *
 * ⚠️ `update` puis `put` de repli, et non un `put` d'un objet reconstruit : deux réglages
 * commutés coup sur coup depuis le même écran recomposeraient chacun la ligne entière depuis ce
 * qu'ils avaient lu, et le second effacerait le premier.
 *
 * Contrairement à la lecture, l'écriture **rejette** : c'est l'écran qui décide quoi en dire, et
 * un interrupteur qui revient à sa place est le seul aveu honnête d'un réglage non enregistré.
 */
export async function saveDevicePreference<Key extends keyof ResolvedDevicePreferences>(
  db: SharedDatabase,
  key: Key,
  value: ResolvedDevicePreferences[Key],
): Promise<void> {
  const changed = await db.preferences.update(ROW_ID, { [key]: value })

  if (changed === 0) await db.preferences.put({ id: ROW_ID, [key]: value })
}
